// ============================================
// Lead Detail Page with i18n
// ============================================

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight, Copy, Eye, EyeOff, CheckCircle, XCircle, MessageSquare,
  AlertTriangle, Loader2, Building2, FileText, Clock, CreditCard, User, MapPin, Mail, Hash
} from 'lucide-react';
import { useAuthStore, useToast } from '../stores';
import { leadsApi, orderPacksApi, messagesApi } from '../lib/api';
import { PageLoader, ProjectBadge, Modal, ConfirmDialog, ErrorState, StageBadge } from '../components';
import { cn, formatDateTime, stageLabels, copyToClipboard, paymentMethodLabels, tierLabels, formatCurrency } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

export function LeadDetail() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const userRole = useAuthStore((state) => state.user?.role);

  const [showRevealedData, setShowRevealedData] = useState(false);
  const [revealedData, setRevealedData] = useState<{ 
    phone: string; 
    national_id?: string;
    bank_name?: string;
    branch_number?: string;
    account_number?: string;
  } | null>(null);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [eligibilityNotes, setEligibilityNotes] = useState('');
  const [showConfirmHot, setShowConfirmHot] = useState(false);

  const { data: leadData, isLoading, error, refetch } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsApi.get(leadId!),
    enabled: !!leadId,
  });

  const { data: orderPackData } = useQuery({
    queryKey: ['order-pack', leadId],
    queryFn: () => orderPacksApi.get(leadId!),
    enabled: !!leadId,
  });

  const revealMutation = useMutation({
    mutationFn: () => leadsApi.revealData(leadId!),
    onSuccess: (data) => {
      setRevealedData(data);
      setShowRevealedData(true);
      toast.warning(t('lead.dataRevealed'), t('lead.actionLogged'));
    },
    onError: (error) => toast.error(t('common.error'), String(error)),
  });

  const eligibilityMutation = useMutation({
    mutationFn: ({ approved, notes }: { approved: boolean; notes?: string }) =>
      leadsApi.setEligibility(leadId!, approved, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      setShowEligibilityModal(false);
      toast.success(t('lead.eligibilityUpdated'));
    },
    onError: (error) => toast.error(t('common.error'), String(error)),
  });

  const enterHotMutation = useMutation({
    mutationFn: () => leadsApi.markEnteredHot(leadId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      setShowConfirmHot(false);
      toast.success(t('lead.enteredInHot'));
    },
    onError: (error) => toast.error(t('common.error'), String(error)),
  });

  const copyOrderPackMutation = useMutation({
    mutationFn: () => orderPacksApi.getText(leadId!),
    onSuccess: async (data) => {
      const copied = await copyToClipboard(data.text);
      if (copied) toast.success(t('lead.copied'), t('lead.copiedDesc'));
      else toast.error(t('common.error'), t('lead.copyFailed'));
    },
    onError: (error) => toast.error(t('common.error'), String(error)),
  });

  if (isLoading) return <PageLoader />;
  if (error || !leadData) return <ErrorState onRetry={refetch} />;

  const lead = leadData.lead;
  const orderPack = orderPackData?.orderPack;
  const isOwner = userRole === 'owner';
  const name = lead.contact ? `${lead.contact.name_first} ${lead.contact.name_last}`.trim() || t('common.unknown') : t('common.unknown');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-icon" aria-label={t('common.back')}><ArrowRight className="w-5 h-5 rtl-flip" /></button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">{name}</h1>
            <ProjectBadge projectId={lead.project_id} />
            <StageBadge stage={lead.stage} />
          </div>
          <p className="text-slate-400 mt-1">{t('lead.created')}: {formatDateTime(lead.created_at)}</p>
        </div>
        <button onClick={() => navigate(`/whatsapp?lead=${leadId}`)} className="btn-secondary"><MessageSquare className="w-4 h-4" />{t('lead.chat')}</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4">{t('lead.contactInfo')}</h2>
            <div className="grid grid-cols-2 gap-4">
              <InfoItem label={t('lead.phone')}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{showRevealedData && revealedData ? revealedData.phone : lead.contact?.phone_e164_masked || '***-***-****'}</span>
                  {isOwner && (
                    <button onClick={() => showRevealedData ? setShowRevealedData(false) : revealMutation.mutate()} className="btn-icon text-slate-400 hover:text-primary-400" disabled={revealMutation.isPending}>
                      {revealMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : showRevealedData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </InfoItem>
              <InfoItem label={t('lead.name')}>{name}</InfoItem>
              {lead.contact?.email && <InfoItem label="البريد الإلكتروني"><span className="text-slate-300">{lead.contact.email}</span></InfoItem>}
              {lead.contact?.city && <InfoItem label="المدينة"><span className="text-slate-300">{lead.contact.city}</span></InfoItem>}
              <InfoItem label={t('lead.source')}>{lead.source}</InfoItem>
              <InfoItem label={t('lead.stage')}><StageBadge stage={lead.stage} /></InfoItem>
            </div>
            
            {/* National ID Section */}
            <div className="mt-4 pt-4 border-t border-slate-800">
              <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2"><Hash className="w-4 h-4" />رقم الهوية</h3>
              <div className="flex items-center gap-2">
                <span className="font-mono text-white">
                  {showRevealedData && revealedData?.national_id 
                    ? revealedData.national_id 
                    : lead.contact?.national_id_masked || '***-***-***'}
                </span>
              </div>
            </div>
            
            {lead.notes && <div className="mt-4 pt-4 border-t border-slate-800"><InfoItem label={t('lead.notes')}><p className="text-slate-300">{lead.notes}</p></InfoItem></div>}
          </div>

          {/* Bank Details Section */}
          {(lead.bank_details || (showRevealedData && revealedData?.bank_name)) && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary-400" />
                تفاصيل الحساب البنكي
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoItem label="اسم البنك">
                  <span className="text-white">
                    {showRevealedData && revealedData?.bank_name 
                      ? revealedData.bank_name 
                      : lead.bank_details?.bank_name || '-'}
                  </span>
                </InfoItem>
                <InfoItem label="رقم الفرع">
                  <span className="font-mono text-white">
                    {showRevealedData && revealedData?.branch_number 
                      ? revealedData.branch_number 
                      : lead.bank_details?.branch_number || '-'}
                  </span>
                </InfoItem>
                <InfoItem label="رقم الحساب">
                  <span className="font-mono text-white">
                    {showRevealedData && revealedData?.account_number 
                      ? revealedData.account_number 
                      : lead.bank_details?.account_number_masked || '***-***-***'}
                  </span>
                </InfoItem>
              </div>
            </div>
          )}

          {lead.product && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-white mb-4">{t('lead.product')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <InfoItem label={t('lead.product')}>{lead.product.title}</InfoItem>
                <InfoItem label={t('lead.cashPrice')}>{formatCurrency(lead.product.price_cash)}</InfoItem>
              </div>
            </div>
          )}

          {orderPack && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">{t('lead.orderPack')}</h2>
                <button onClick={() => copyOrderPackMutation.mutate()} className="btn-secondary text-sm" disabled={copyOrderPackMutation.isPending}>
                  {copyOrderPackMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Copy className="w-4 h-4" />{t('lead.copyForHot')}</>}
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoItem label={t('lead.product')}>{orderPack.product_title}</InfoItem>
                <InfoItem label={t('lead.cashPrice')}>{formatCurrency(orderPack.price_cash)}</InfoItem>
                <InfoItem label={t('lead.installments')}>{orderPack.installments} {t('lead.installment')}</InfoItem>
                <InfoItem label="Tier"><span className={cn('badge', orderPack.tier === 'A' ? 'badge-green' : 'badge-amber')}>{tierLabels[orderPack.tier]}</span></InfoItem>
                <InfoItem label={t('lead.monthlyPayment')}><span className="text-lg font-bold text-primary-400">{formatCurrency(orderPack.monthly_payment)}</span></InfoItem>
                <InfoItem label={t('lead.total')}>{formatCurrency(orderPack.total_price)}</InfoItem>
                <InfoItem label={t('lead.paymentMethod')}>{paymentMethodLabels[orderPack.payment_method] || orderPack.payment_method}</InfoItem>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-800">
                {orderPack.entered_in_hot ? (
                  <div className="flex items-center gap-2 text-green-400"><CheckCircle className="w-5 h-5" /><span>{t('lead.enteredInHotStatus')}</span></div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-amber-400"><AlertTriangle className="w-5 h-5" /><span>{t('lead.notEnteredInHot')}</span></div>
                    <button onClick={() => setShowConfirmHot(true)} className="btn-success"><Building2 className="w-4 h-4" />{t('lead.confirmHotEntry')}</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="font-semibold text-white mb-3">{t('lead.actions')}</h3>
            {lead.stage === 'waiting_eligibility' && (
              <div className="space-y-2">
                <button onClick={() => setShowEligibilityModal(true)} className="btn-success w-full"><CheckCircle className="w-4 h-4" />{t('lead.approve')}</button>
                <button onClick={() => eligibilityMutation.mutate({ approved: false })} className="btn-danger w-full" disabled={eligibilityMutation.isPending}><XCircle className="w-4 h-4" />{t('lead.reject')}</button>
              </div>
            )}
            {lead.stage === 'approved' && !lead.entered_in_hot && (
              <div className="space-y-2">
                <button onClick={() => copyOrderPackMutation.mutate()} className="btn-secondary w-full" disabled={copyOrderPackMutation.isPending}><Copy className="w-4 h-4" />{t('lead.copyOrderData')}</button>
                <button onClick={() => setShowConfirmHot(true)} className="btn-success w-full"><Building2 className="w-4 h-4" />{t('lead.confirmHotEntry')}</button>
              </div>
            )}
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-white mb-3">{t('lead.timeline')}</h3>
            <div className="space-y-3">
              <TimelineItem icon={<Clock className="w-4 h-4" />} title={t('lead.created')} time={formatDateTime(lead.created_at)} />
              {lead.stage !== 'new' && <TimelineItem icon={<MessageSquare className="w-4 h-4" />} title={t('lead.contacted')} time="-" />}
              {['approved', 'rejected', 'funded', 'closed_won', 'closed_lost'].includes(lead.stage) && (
                <TimelineItem icon={lead.stage === 'rejected' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />} title={stageLabels[lead.stage]} time={formatDateTime(lead.updated_at)} />
              )}
              {lead.entered_in_hot && <TimelineItem icon={<Building2 className="w-4 h-4" />} title={t('lead.enteredInHotStatus')} time="-" success />}
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={showEligibilityModal} onClose={() => setShowEligibilityModal(false)} title={t('lead.confirmEligibility')} footer={<><button onClick={() => setShowEligibilityModal(false)} className="btn-secondary">{t('common.cancel')}</button><button onClick={() => eligibilityMutation.mutate({ approved: true, notes: eligibilityNotes })} className="btn-success" disabled={eligibilityMutation.isPending}>{eligibilityMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4" />{t('lead.confirmApproval')}</>}</button></>}>
        <div className="space-y-4">
          <p className="text-slate-300">{t('lead.eligibilityMessage')}</p>
          <div><label className="label">{t('lead.notesOptional')}</label><textarea value={eligibilityNotes} onChange={(e) => setEligibilityNotes(e.target.value)} className="input resize-none" rows={3} placeholder={t('lead.additionalNotes')} /></div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={showConfirmHot} onClose={() => setShowConfirmHot(false)} onConfirm={() => enterHotMutation.mutate()} title={t('lead.confirmHotEntry')} message={t('lead.hotConfirmMessage')} confirmText={t('lead.yesEntered')} variant="warning" loading={enterHotMutation.isPending} />
    </div>
  );
}

interface InfoItemProps { label: string; children: React.ReactNode; }
function InfoItem({ label, children }: InfoItemProps) {
  return <div><p className="text-sm text-slate-500 mb-1">{label}</p><div className="text-white">{children}</div></div>;
}

interface TimelineItemProps { icon: React.ReactNode; title: string; time: string; success?: boolean; }
function TimelineItem({ icon, title, time, success }: TimelineItemProps) {
  return <div className="flex items-start gap-3"><div className={cn('p-1.5 rounded-full', success ? 'bg-green-500/20 text-green-400' : 'bg-slate-800 text-slate-400')}>{icon}</div><div className="flex-1 min-w-0"><p className="text-sm text-white">{title}</p><p className="text-2xs text-slate-500">{time}</p></div></div>;
}
